import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/button';
import {
  Building2,
  Activity,
  Cpu,
  Zap,
  FileText,
  LogOut,
  Sun,
  Moon,
  Users,
  Menu,
  X,
  LayoutGrid,
  Clock,
  HelpCircle,
  ShieldCheck,
  Shield,
  HardDrive,
  Database,
  KeyRound,
  MonitorPlay,
  Droplets,
  FileBarChart,
  Gauge,
} from 'lucide-react';
import { ScanLine } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { ClientsTab } from './ClientsTab';
import { ScannersTab } from './ScannersTab';
import { PLCsTab } from './PLCsTab';
import { AdvancedPDFTemplateEditor } from './AdvancedPDFTemplateEditor';
import { UsersTab } from './UsersTab';
import { WidgetsTab } from './WidgetsTab';
import { PollingIntervalConfig } from '../PollingIntervalConfig';
import { AdminTicketsTab } from './AdminTicketsTab';
import { AuditLogsTab } from './AuditLogsTab';
import { SystemLogsTab } from './SystemLogsTab';
import { StorageCapacityTab } from './StorageCapacityTab';
import { BridgeRegistryTab } from './BridgeRegistryTab';
import { EnergyMetersTab } from './EnergyMetersTab';
import { UserUsageAnalyticsTab } from './UserUsageAnalyticsTab';
import { HMIBuilder } from '../hmi/HMIBuilder';
import { AdminUsersTab } from './AdminUsersTab';
import { PlantKPIConfigTab } from './PlantKPIConfigTab';
import { VFDDrivesTab } from './VFDDrivesTab';
import { ReportBuilderAdminTab } from './ReportBuilderAdminTab';

interface SuperAdminPortalProps {
  session?: any;
  onLogout: () => void;
}

export function SuperAdminPortal({ session, onLogout }: SuperAdminPortalProps) {
  const { theme, toggleTheme } = useTheme();
  const isSuperAdmin = (session?.user?.role || '') === 'super_admin';
  const allowedTabs: string[] = session?.user?.permissions?.adminTabs || [];
  const canSee = (tabId: string) => isSuperAdmin || allowedTabs.includes(tabId);
  const [activeTab, setActiveTab] = useState(() =>
    isSuperAdmin ? 'clients' : (allowedTabs[0] || '')
  );
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.innerWidth >= 1024;
  });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.innerWidth >= 1024;
  });
  const dashboardLogoSrc = './img/logo.png';

  const tabs = [
    { id: 'clients', label: 'Clients', mobileLabel: 'Clients', icon: Users },
    { id: 'plcs', label: 'PLCs', mobileLabel: 'PLCs', icon: Cpu },
    { id: 'energy-meters', label: 'Energy Meters', mobileLabel: 'Energy', icon: Zap },
    { id: 'vfd-drives', label: 'VFD Drives', mobileLabel: 'VFDs', icon: Gauge },
    { id: 'scanners', label: 'Scanners', mobileLabel: 'Scanners', icon: ScanLine },
    { id: 'widgets', label: 'Widgets', mobileLabel: 'Widgets', icon: LayoutGrid },
    { id: 'settings', label: 'Settings', mobileLabel: 'Settings', icon: Clock },
    { id: 'advanced-pdf', label: 'Advanced PDF', mobileLabel: 'PDF', icon: FileText },
    { id: 'users', label: 'Users', mobileLabel: 'Users', icon: Building2 },
    { id: 'tickets', label: 'Support Tickets', mobileLabel: 'Tickets', icon: HelpCircle },
    { id: 'audit-logs', label: 'Audit Logs', mobileLabel: 'Audit', icon: ShieldCheck },
    { id: 'usage-analytics', label: 'Usage Analytics', mobileLabel: 'Usage', icon: Activity },
    { id: 'system-logs', label: 'System Logs', mobileLabel: 'System', icon: HardDrive },
    { id: 'storage-analytics', label: 'Storage Analytics', mobileLabel: 'Storage', icon: Database },
    { id: 'bridge-registry', label: 'Bridge Registry', mobileLabel: 'Bridge', icon: KeyRound },
    { id: 'hmi-builder',    label: 'HMI Builder',     mobileLabel: 'HMI',    icon: MonitorPlay },
    { id: 'plant-kpi-config', label: 'Plant KPI Config', mobileLabel: 'Plant KPI', icon: Droplets },
    { id: 'report-builder', label: 'Report Builder', mobileLabel: 'Reports', icon: FileBarChart },
    // super_admin only — never shown to admin_staff
    ...(isSuperAdmin ? [{ id: 'admin-users', label: 'Admin Users', mobileLabel: 'Staff', icon: Shield }] : []),
  ].filter(tab =>
    // super_admin sees all tabs; admin_staff sees only their allowed tabs (plus management of their own allowed set can't modify this logic)
    isSuperAdmin || allowedTabs.includes(tab.id)
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const syncSidebarState = (event?: MediaQueryListEvent) => {
      const matches = event?.matches ?? mediaQuery.matches;
      setIsDesktop(matches);
      setSidebarOpen(matches);
    };

    syncSidebarState();
    mediaQuery.addEventListener('change', syncSidebarState);
    return () => mediaQuery.removeEventListener('change', syncSidebarState);
  }, []);

  const handleTabSelect = (tabId: string) => {
    setActiveTab(tabId);
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 shadow-sm"
      >
        <div className="flex min-h-20 flex-wrap items-center gap-3 px-4 py-3 sm:px-6 lg:h-24 lg:flex-nowrap lg:py-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <motion.div
            className="flex min-w-0 flex-1 items-center lg:flex-none"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <img
              src={dashboardLogoSrc}
              alt="Factreon"
              className="h-12 w-auto max-w-full object-contain sm:h-14 lg:h-20"
              style={{ maxWidth: '320px' }}
            />
          </motion.div>

          {/* Watermark badge */}
          <div className="hidden xl:flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-3 py-1.5">
            <span className="text-[11px] font-semibold tracking-wide text-muted-foreground/70">A Waterwalk Creation</span>
          </div>

          <Button variant="ghost" size="icon" onClick={toggleTheme} className="shrink-0">
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          <Button variant="destructive" onClick={onLogout} className="gap-2 shrink-0 px-3 sm:px-4">
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex">
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-30 bg-background/70 backdrop-blur-sm lg:hidden"
          />
        )}

        {/* Sidebar Navigation */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 20 }}
              className="fixed top-0 z-40 flex flex-col h-screen w-[85vw] max-w-64 border-r bg-card/95 backdrop-blur-sm lg:sticky lg:top-24 lg:h-[calc(100vh-6rem)] lg:w-64 lg:max-w-none lg:bg-card/50"
            >
              <nav className="flex-1 overflow-y-auto space-y-1 p-4 pt-20 lg:pt-4">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabSelect(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${isActive
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                        : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      <span className="min-w-0 truncate text-sm font-medium leading-tight">{isDesktop ? tab.label : tab.mobileLabel}</span>
                      {isActive && (
                        <div className="ml-auto h-2 w-2 shrink-0 rounded-full bg-white" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Content Area */}
        <main className="min-w-0 flex-1 overflow-x-hidden p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="sa-dashboard"
            >
              {activeTab === 'clients'           && canSee('clients')           && <ClientsTab />}
              {activeTab === 'plcs'              && canSee('plcs')              && <PLCsTab />}
              {activeTab === 'energy-meters'     && canSee('energy-meters')     && <EnergyMetersTab />}
              {activeTab === 'scanners'          && canSee('scanners')          && <ScannersTab />}
              {activeTab === 'widgets'           && canSee('widgets')           && <WidgetsTab />}
              {activeTab === 'settings'          && canSee('settings')          && <PollingIntervalConfig token={session?.token} />}
              {activeTab === 'advanced-pdf'      && canSee('advanced-pdf')      && <AdvancedPDFTemplateEditor />}
              {activeTab === 'users'             && isSuperAdmin                && <UsersTab />}
              {activeTab === 'tickets'           && canSee('tickets')           && <AdminTicketsTab token={session?.token || ''} />}
              {activeTab === 'audit-logs'        && canSee('audit-logs')        && <AuditLogsTab token={session?.token || ''} />}
              {activeTab === 'usage-analytics'   && canSee('usage-analytics')   && <UserUsageAnalyticsTab token={session?.token || ''} />}
              {activeTab === 'system-logs'       && canSee('system-logs')       && <SystemLogsTab token={session?.token || ''} />}
              {activeTab === 'storage-analytics' && canSee('storage-analytics') && <StorageCapacityTab token={session?.token || ''} />}
              {activeTab === 'bridge-registry'   && canSee('bridge-registry')   && <BridgeRegistryTab token={session?.token || ''} />}
              {activeTab === 'hmi-builder'       && canSee('hmi-builder')       && <HMIBuilder token={session?.token || ''} />}
              {activeTab === 'plant-kpi-config'  && canSee('plant-kpi-config')  && <PlantKPIConfigTab />}
              {activeTab === 'vfd-drives'        && canSee('vfd-drives')        && <VFDDrivesTab />}
              {activeTab === 'report-builder'    && canSee('report-builder')    && <ReportBuilderAdminTab token={session?.token || ''} />}
              {activeTab === 'admin-users'       && isSuperAdmin                && <AdminUsersTab />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>

  );
}